import { useState, useRef, useEffect, useCallback } from "react";
import { 
  MoreHorizontal, 
  Share2, 
  Pencil, 
  Trash2, 
  MessageSquare,
  FileText,
  Star,
  Copy,
  ExternalLink,
  X,
  Link2
} from "lucide-react";

/**
 * QuickActions - A floating action menu with keyboard shortcuts
 * 
 * Usage:
 * <QuickActions
 *   actions={[
 *     { icon: Share2, label: "Share", onClick: () => {}, color: "green", shortcut: "S" },
 *     { icon: Pencil, label: "Edit", onClick: () => {}, color: "indigo", shortcut: "E" },
 *     { icon: Trash2, label: "Delete", onClick: () => {}, color: "red", confirm: true, shortcut: "D" },
 *   ]}
 *   position="right" // "left" | "right" | "top" | "bottom"
 *   enableKeyboardShortcuts={true}
 * />
 * 
 * Keyboard Shortcuts (when menu is open):
 * - S: Share
 * - E: Edit
 * - D: Delete
 * - Escape: Close menu
 */

const COLOR_CLASSES = {
  slate: "hover:text-slate-400 hover:bg-slate-700/50",
  indigo: "hover:text-indigo-400 hover:bg-indigo-500/10",
  green: "hover:text-green-400 hover:bg-green-500/10",
  red: "hover:text-red-400 hover:bg-red-500/10",
  purple: "hover:text-purple-400 hover:bg-purple-500/10",
  blue: "hover:text-blue-400 hover:bg-blue-500/10",
  yellow: "hover:text-yellow-400 hover:bg-yellow-500/10",
  orange: "hover:text-orange-400 hover:bg-orange-500/10",
};

export default function QuickActions({ 
  actions = [], 
  position = "right",
  triggerIcon: TriggerIcon = MoreHorizontal,
  className = "",
  enableKeyboardShortcuts = true
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  // Handle keyboard shortcuts when menu is open
  const handleKeyDown = useCallback((event) => {
    if (!isOpen || !enableKeyboardShortcuts) return;
    
    const key = event.key.toUpperCase();
    
    // Escape to close
    if (event.key === "Escape") {
      setIsOpen(false);
      setConfirmAction(null);
      return;
    }
    
    // If in confirm mode, handle Y/N
    if (confirmAction) {
      if (key === "Y" || event.key === "Enter") {
        confirmAction.onClick?.();
        setIsOpen(false);
        setConfirmAction(null);
      } else if (key === "N" || event.key === "Escape") {
        setConfirmAction(null);
      }
      return;
    }
    
    // Find action matching the shortcut
    const action = actions.find(a => a.shortcut?.toUpperCase() === key);
    if (action && !action.disabled) {
      event.preventDefault();
      if (action.confirm) {
        setConfirmAction(action);
      } else {
        action.onClick?.();
        setIsOpen(false);
      }
    }
  }, [isOpen, enableKeyboardShortcuts, actions, confirmAction]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setIsOpen(false);
        setConfirmAction(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleAction = (action) => {
    if (action.confirm && !confirmAction) {
      setConfirmAction(action);
      return;
    }
    
    action.onClick?.();
    setIsOpen(false);
    setConfirmAction(null);
  };

  const getPositionClasses = () => {
    switch (position) {
      case "left":
        return "right-full mr-2 top-0";
      case "top":
        return "bottom-full mb-2 left-1/2 -translate-x-1/2";
      case "bottom":
        return "top-full mt-2 left-1/2 -translate-x-1/2";
      case "right":
      default:
        return "left-full ml-2 top-0";
    }
  };

  if (actions.length === 0) return null;

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        onClick={() => {
          setIsOpen(!isOpen);
          setConfirmAction(null);
        }}
        className="p-1.5 text-slate-500 hover:text-slate-300 hover:bg-slate-700/50 rounded-lg transition-all"
        data-testid="quick-actions-trigger"
      >
        <TriggerIcon className="w-4 h-4" />
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className={`absolute z-50 ${getPositionClasses()} bg-slate-800 border border-slate-700 rounded-xl shadow-xl py-1 min-w-[160px] animate-in fade-in zoom-in-95 duration-150`}
        >
          {confirmAction ? (
            <div className="p-3">
              <p className="text-sm text-slate-300 mb-3">
                {confirmAction.confirmText || `Are you sure?`}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="flex-1 px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200 border border-slate-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleAction({ ...confirmAction, confirm: false })}
                  className="flex-1 px-3 py-1.5 text-xs bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                >
                  Confirm
                </button>
              </div>
            </div>
          ) : (
            <>
              {actions.map((action, index) => {
                const Icon = action.icon;
                const colorClass = COLOR_CLASSES[action.color || "slate"];
                
                return (
                  <button
                    key={index}
                    onClick={() => handleAction(action)}
                    disabled={action.disabled}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-400 ${colorClass} transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid={`quick-action-${action.label?.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                    <span>{action.label}</span>
                    {action.shortcut && (
                      <span className="ml-auto text-xs text-slate-600">{action.shortcut}</span>
                    )}
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Pre-built action sets for common use cases with keyboard shortcuts
export const clientActions = (handlers) => [
  { icon: Link2, label: "Portal Invite", onClick: handlers.onPortalInvite, color: "blue", shortcut: "I" },
  { icon: Share2, label: "Share to Team", onClick: handlers.onShare, color: "green", shortcut: "S" },
  { icon: MessageSquare, label: "Generate Reply", onClick: handlers.onGenerateReply, color: "purple", shortcut: "G" },
  { icon: Pencil, label: "Edit", onClick: handlers.onEdit, color: "indigo", shortcut: "E" },
  { icon: Trash2, label: "Delete", onClick: handlers.onDelete, color: "red", confirm: true, confirmText: "Delete this client? (Y/N)", shortcut: "D" },
];

export const projectActions = (handlers) => [
  { icon: Share2, label: "Share to Team", onClick: handlers.onShare, color: "green", shortcut: "S" },
  { icon: FileText, label: "New Proposal", onClick: handlers.onNewProposal, color: "blue", shortcut: "P" },
  { icon: Star, label: "Request Testimonial", onClick: handlers.onRequestTestimonial, color: "yellow", shortcut: "T" },
  { icon: Pencil, label: "Edit", onClick: handlers.onEdit, color: "indigo", shortcut: "E" },
  { icon: Trash2, label: "Delete", onClick: handlers.onDelete, color: "red", confirm: true, confirmText: "Delete this project? (Y/N)", shortcut: "D" },
];

export const proposalActions = (handlers) => [
  { icon: Share2, label: "Share Link", onClick: handlers.onShare, color: "green", shortcut: "S" },
  { icon: Copy, label: "Duplicate", onClick: handlers.onDuplicate, color: "purple", shortcut: "C" },
  { icon: ExternalLink, label: "Export", onClick: handlers.onExport, color: "blue", shortcut: "X" },
  { icon: Pencil, label: "Edit", onClick: handlers.onEdit, color: "indigo", shortcut: "E" },
  { icon: Trash2, label: "Delete", onClick: handlers.onDelete, color: "red", confirm: true, confirmText: "Delete this proposal? (Y/N)", shortcut: "D" },
];
